import PropTypes from "prop-types";
import NumberInput from "@/components/input/number.input.jsx";
import NumberSliderInput from "@/components/input/number-slider.input.jsx";
import Picker from "@/components/input/picker.input.jsx";
import YesNoInput from "@/components/input/yes-no.input.jsx";
import CustomizeSection from "@/components/overlays/shared/customize-section.jsx";
import DevCustomizeOptions from "@/components/shared/dev-customize-options.jsx";
import { isYes } from "@/util/common.js";

CustomizeArtSpaceBallPit.propTypes = {
  setConfigState: PropTypes.func.isRequired,
  config: PropTypes.object.isRequired,
  resetToDefault: PropTypes.func.isRequired,
  setRate: PropTypes.func.isRequired,
  rate: PropTypes.number.isRequired,
  type: PropTypes.oneOf(["ball", "art"]),
};

export default function CustomizeArtSpaceBallPit({
  setConfigState,
  config,
  resetToDefault,
  setRate,
  rate,
  type = "ball",
}) {
  const setStartingBallSize = (event) => {
    const ballSize = event.target.value;

    if (ballSize >= Number(config.maxScale)) {
      setConfigState({ target: { name: "maxScale", value: ballSize + 1 } });
    }
    event.target.value = ballSize;
    setConfigState(event);
  };

  const setMaxScale = (event) => {
    const ballSize = event.target.value;

    if (ballSize <= Number(config.startSize)) {
      setConfigState({ target: { name: "startSize", value: ballSize - 1 } });
    }

    setConfigState(event);
  };

  const setMaxDonationAmount = (event) => {
    const maxDonationAmount = Number(event.target.value);

    if (maxDonationAmount <= Number(config.minAmount)) {
      setConfigState({
        target: { name: "minAmount", value: maxDonationAmount - 1 },
      });
    }

    setConfigState(event);
  };

  const setMinDonationAmount = (event) => {
    const minDonationAmount = Number(event.target.value);

    if (minDonationAmount >= Number(config.maxAmount)) {
      setConfigState({
        target: { name: "maxAmount", value: minDonationAmount + 1 },
      });
    }

    setConfigState(event);
  };

  const typeOptions = [
    {
      name: "Pizza",
      value: "pizza",
    },
    {
      name: "Football",
      value: "football",
    },
    {
      name: "Heart",
      value: "heart",
    },
    {
      name: "UFO",
      value: "ufo",
    },
    {
      name: "Spaceship",
      value: "spaceship",
    },
  ];

  return (
    <>
      {type === "art" && (
        <CustomizeSection title="Appearance">
          <div className="mb-5">
            <Picker
              items={typeOptions}
              fieldName="ballType"
              onChange={setConfigState}
              currentValue={config.ballType}
              labelText="Art Type"
            />
          </div>
        </CustomizeSection>
      )}
      <CustomizeSection title="Sizing">
        <YesNoInput
          mode="checkbox"
          onChange={setConfigState}
          currentValue={config.scaleSize}
          name="scaleSize"
          label={`Scale the size of the ${type === "art" ? "art piece" : "ball"} relative to the donation amount`}
        />

        <div className={`collapse ${isYes(config.scaleSize) ? "collapse-open" : "collapse-close"}`}>
          <div className="collapse-content px-0 user-select-none">
            <div className="my-6">
              <NumberSliderInput
                disabled={!isYes(config.scaleSize)}
                fieldName="startSize"
                value={Number(config.startSize)}
                onChange={setStartingBallSize}
                minValue={1}
                step={1}
                maxValue={10}
                labelText={`The starting size of the ${type === "art" ? "art piece" : "ball"}`}
                showValueSuffix={false}
              />
            </div>

            <div className="mb-8">
              <NumberSliderInput
                disabled={!isYes(config.scaleSize)}
                fieldName="maxScale"
                value={Number(config.maxScale)}
                onChange={setMaxScale}
                minValue={Number(config.startSize) + 1}
                step={1}
                maxValue={Number(config.startSize) + 20}
                labelText={`The maximum size of the ${type === "art" ? "art piece" : "ball"}`}
                showValueSuffix={false}
              />
            </div>

            <div className="mb-6">
              <NumberInput
                name="maxAmount"
                value={Number(config.maxAmount)}
                placeholder="100"
                onChange={setMaxDonationAmount}
                isCurrency={true}
                minValue={100}
                maxValue={1000}
                labelText="Average large donation amount"
              />
            </div>

            <NumberInput
              name="minAmount"
              value={Number(config.minAmount)}
              placeholder="5"
              onChange={setMinDonationAmount}
              isCurrency={true}
              minValue={1}
              maxValue={100}
              labelText={`Minimum donation amount to create a new ${type === "art" ? "art piece" : "ball"}`}
            />
          </div>
        </div>
      </CustomizeSection>

      <CustomizeSection title="Advanced">
        <NumberSliderInput
          fieldName="clampVelocity"
          value={Number(config.clampVelocity)}
          onChange={setConfigState}
          minValue={0.0}
          step={0.5}
          maxValue={100}
          labelText={`The ${type === "art" ? "art piece" : "ball"}'s max velocity`}
          showValueSuffix={true}
        />

        <NumberSliderInput
          fieldName="clampAngularSpeed"
          value={Number(config.clampAngularSpeed)}
          onChange={setConfigState}
          minValue={0.0}
          step={0.01}
          maxValue={1}
          labelText={`The ${type === "art" ? "art piece" : "ball"}'s max angular speed`}
          showValueSuffix={true}
        />

        <NumberSliderInput
          fieldName="explosionForceMagnitude"
          value={Number(config.explosionForceMagnitude)}
          onChange={setConfigState}
          minValue={0.001}
          step={0.001}
          maxValue={0.3}
          labelText={`The ${type === "art" ? "art piece" : "ball"}'s explosion force`}
          showValueSuffix={true}
        />
      </CustomizeSection>

      <CustomizeSection title="Misc.">
        {type === "ball" && (
          <>
            <YesNoInput
              mode="checkbox"
              onChange={setConfigState}
              currentValue={config.randomColors}
              name="randomColors"
              label="Use random colors"
            />

            <YesNoInput
              mode="checkbox"
              onChange={setConfigState}
              currentValue={config.showNames}
              name="showNames"
              label="Show donor names"
            />
          </>
        )}

        <div className="flex justify-between">
          <div className="mt-1 flex-grow">
            <button type="reset" onClick={resetToDefault} className="btn btn-secondary mt-2 w-full">
              Reset to default
            </button>
          </div>
        </div>
      </CustomizeSection>

      <DevCustomizeOptions setConfigState={setConfigState} config={config}>
        <div className="my-6">
          <NumberSliderInput
            fieldName="rate"
            value={rate}
            onChange={(e) => setRate(parseFloat(e.target.value))}
            minValue={1}
            step={1}
            maxValue={10}
            labelText={`How many seconds to wait before adding ${type === "art" ? "an art piece" : "a ball"} (demo)`}
            showValueSuffix={true}
          />
        </div>
        <div className="mb-6">
          <NumberInput
            name="maxBalls"
            value={Number(config.maxBalls)}
            placeholder="25"
            onChange={setConfigState}
            isCurrency={false}
            maxLength={4}
            labelText={`The maximum amount of ${type === "art" ? "art pieces" : "balls"}`}
          />
        </div>
        <div className="my-6">
          <NumberSliderInput
            fieldName="mass"
            value={Number(config.mass)}
            onChange={setConfigState}
            minValue={1}
            step={10}
            maxValue={10000}
            labelText={`The ${type === "art" ? "art piece" : "ball"}'s mass`}
            showValueSuffix={true}
          />
        </div>
        <div className="my-6">
          <NumberSliderInput
            fieldName="density"
            value={Number(config.density)}
            onChange={setConfigState}
            minValue={1}
            step={10}
            maxValue={10000}
            labelText={`The ${type === "art" ? "art piece" : "ball"}'s density`}
            showValueSuffix={true}
          />
        </div>
        <div className="my-6">
          <NumberSliderInput
            fieldName="friction"
            value={Number(config.friction)}
            onChange={setConfigState}
            minValue={0.0}
            step={0.0001}
            maxValue={1}
            labelText={`The ${type === "art" ? "art piece" : "ball"}'s friction`}
            showValueSuffix={true}
          />
        </div>
        <div className="my-6">
          <NumberSliderInput
            fieldName="frictionAir"
            value={Number(config.frictionAir)}
            onChange={setConfigState}
            minValue={0.0}
            step={0.0001}
            maxValue={1}
            labelText={`The ${type === "art" ? "art piece" : "ball"}'s friction in the air`}
            showValueSuffix={true}
          />
        </div>
        <div className="my-6">
          <NumberSliderInput
            fieldName="frictionStatic"
            value={Number(config.frictionStatic)}
            onChange={setConfigState}
            minValue={0.0}
            step={0.0001}
            maxValue={1}
            labelText={`The ${type === "art" ? "art piece" : "ball"}'s friction when static`}
            showValueSuffix={true}
          />
        </div>
        <div className="my-6">
          <NumberSliderInput
            fieldName="restitution"
            value={Number(config.restitution)}
            onChange={setConfigState}
            minValue={0.0}
            step={0.1}
            maxValue={1}
            labelText={`The ${type === "art" ? "art piece" : "ball"}'s restitution`}
            showValueSuffix={true}
          />
        </div>
      </DevCustomizeOptions>
    </>
  );
}
